import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { Check, X, Search, Mail, Save, Loader2 } from 'lucide-react';

export interface BullhornNoteEditorProps {
    mode: 'candidate' | 'vacancy'; // Context from which it was opened
    targetId: string; // Either candidateId or vacancyId
    currentUser: any;
    onClose: () => void;
    onSaved: () => void;
    // Optional initial data if editing existing note
    initialData?: {
        id: string;
        content: string;
        action_type?: string;
        next_action_type?: string;
        linkedCandidateId?: string;
        linkedVacancyId?: string;
    };
}

const ACTIONS = [
    'Prescreening', 'Intakegesprek', 'Aanbieding', 'Sollicitatiegesprek',
    'Referentiecheck', 'Afgewezen', 'Aangenomen', 'Overig'
];

export const BullhornNoteEditor: React.FC<BullhornNoteEditorProps> = ({ mode, targetId, currentUser, onClose, onSaved, initialData }) => {
    const [content, setContent] = useState(initialData?.content || '');
    const [action, setAction] = useState(initialData?.action_type || 'Prescreening');
    const [nextAction, setNextAction] = useState(initialData?.next_action_type || 'Geen');
    
    // Linking state
    const [linkedCandidateId, setLinkedCandidateId] = useState<string>(mode === 'candidate' ? targetId : (initialData?.linkedCandidateId || ''));
    const [linkedVacancyId, setLinkedVacancyId] = useState<string>(mode === 'vacancy' ? targetId : (initialData?.linkedVacancyId || ''));
    const [notifyEmail, setNotifyEmail] = useState('');

    // Dropdown options
    const [candidates, setCandidates] = useState<any[]>([]);
    const [vacancies, setVacancies] = useState<any[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        // Fetch basic candidate list and vacancy list for the dropdowns
        const fetchDropdowns = async () => {
             const { data: candData } = await supabase.from('candidates').select('id, name').order('name');
             if (candData) setCandidates(candData);
             
             const { data: vacData } = await supabase.from('vacancies').select('id, title').order('title');
             if (vacData) setVacancies(vacData);
        };
        fetchDropdowns();
    }, []);

    const handleSave = async () => {
        if (!content.trim() || !currentUser?.id) return;
        if (String(currentUser.id) === 'current-user') {
            alert('Gebruiker niet geladen. Vernieuw de pagina en probeer opnieuw.');
            return;
        }
        setIsSaving(true);
        try {
            const table = mode === 'candidate' ? 'candidate_notes' : 'vacancy_notes';
            const authorName =
                currentUser.name ||
                currentUser.profile?.name ||
                currentUser.user_metadata?.full_name ||
                currentUser.email ||
                'Onbekend';
            const payload: any = {
                author_id: currentUser.id,
                author_name: authorName,
                content: content,
                action_type: action !== 'Geen' ? action : null,
                next_action_type: nextAction !== 'Geen' ? nextAction : null,
                [mode === 'candidate' ? 'candidate_id' : 'vacancy_id']: targetId,
                ...(mode === 'candidate' && linkedVacancyId ? { vacancy_id: linkedVacancyId } : {}),
                ...(mode === 'vacancy' && linkedCandidateId ? { candidate_id: linkedCandidateId } : {})
            };

            const attemptSave = async (dataPayload: any) => {
                if (initialData?.id) {
                    const { error } = await supabase.from(table).update({ ...dataPayload, updated_at: new Date().toISOString() }).eq('id', initialData.id);
                    if (error) throw error;
                } else {
                    const { error } = await supabase.from(table).insert(dataPayload);
                    if (error) throw error;
                }
            };

            try {
                await attemptSave(payload);
            } catch (err: any) {
                const errText = `${err?.message || ''} ${err?.details || ''} ${err?.hint || ''}`.toLowerCase();
                const missingOptionalCols =
                    errText.includes('action_type') ||
                    errText.includes('next_action_type') ||
                    errText.includes('does not exist') ||
                    errText.includes('schema cache') ||
                    errText.includes('pgrst204');
                if (missingOptionalCols) {
                    console.warn('Retrying note save without optional columns…', err);
                    delete payload.action_type;
                    delete payload.next_action_type;
                    await attemptSave(payload);
                    alert(
                        'Notitie opgeslagen. Actie-/opvolgingsvelden zijn overgeslagen omdat die kolommen in de database ontbreken. Voer desgewenst een migratie uit om ze toe te voegen.'
                    );
                } else {
                    throw err;
                }
            }

            // Notification Email
            if (notifyEmail && notifyEmail.includes('@')) {
                const baseApiUrl = (import.meta as any).env.VITE_API_URL || "http://localhost:4000";
                fetch(`${baseApiUrl}/api/automations/send-email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        to: notifyEmail,
                        subject: `Nieuwe Notitie over kandidaat door ${currentUser?.name || 'collega'}`,
                        html: `Hoi,<br><br>Er is zojuist een notitie achtergelaten door ${currentUser?.name}:<br><br><b>Notitie:</b><br>${content}<br><br><b>Actie:</b> ${action}<br><br>Met vriendelijke groet,<br>Werftools Automations`
                    })
                }).catch(e => console.warn("Failed to send notification email", e));
            }

            // Create Task if requested
            if (nextAction === 'Taak') {
                const todoPayload: any = {
                    title: `Opvolging: Notitie over ${action}`,
                    description: `Deze taak is automatisch aangemaakt vanuit een nieuwe notitie.\n\nNotitie inhoud:\n${content}`,
                    user_id: currentUser.id,
                    status: 'Pending',
                    priority: 'medium',
                    is_external: false,
                    quick_link_url: window.location.pathname + window.location.search,
                    quick_link_title: 'Gekoppelde notitie'
                };
                
                if (mode === 'candidate') {
                    todoPayload.candidate_id = targetId;
                    if (linkedVacancyId) todoPayload.vacancy_id = linkedVacancyId;
                } else if (mode === 'vacancy') {
                    todoPayload.vacancy_id = targetId;
                    if (linkedCandidateId) todoPayload.candidate_id = linkedCandidateId;
                }
                
                const { error: todoErr } = await supabase.from('todos').insert(todoPayload);
                if (todoErr) console.warn("Failed to create todo:", todoErr);
            }

            onSaved();
        } catch (e: any) {
            alert(`Fout bij opslaan notitie: ${e.message || e}`);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col lg:flex-row h-full min-h-[500px] bg-white animate-fadeIn w-full relative">
            
            {/* Left Column: Rich Text Editor Area */}
            <div className="w-full lg:w-3/5 flex flex-col p-6 border-b lg:border-b-0 lg:border-r border-slate-100 h-full">
                <div className="flex border-b border-slate-200 mb-4 items-center gap-6">
                    <h3 className="text-lg font-black text-slate-800 pb-2">Notitie bewerken</h3>
                </div>

                <div className="flex items-center gap-6 mb-4">
                    <button className="flex items-center text-[11px] font-black text-slate-800 border-b-2 border-[#e51b23] pb-2 gap-2 tracking-widest uppercase">
                        Notitiesjabloon <Check size={14} className="text-emerald-500"/>
                    </button>
                    <button className="flex items-center text-[11px] font-black tracking-widest uppercase text-slate-400 pb-2 hover:text-slate-600 transition-colors">
                        Concepten (0)
                    </button>
                </div>

                <label className="text-[12px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Opmerkingen</label>
                <div className="flex-1 border border-slate-200 rounded-md overflow-hidden flex flex-col bg-slate-50 relative min-h-[300px]">
                    
                    {/* Fake Toolbar to match visual */}
                    <div className="flex gap-2 items-center p-2 border-b border-slate-200 bg-white overflow-x-auto no-scrollbar">
                        <select className="text-[12px] p-1 border border-transparent hover:border-slate-200 rounded outline-none text-slate-600 cursor-pointer bg-white">
                            <option>Stijl</option>
                        </select>
                        <select className="text-[12px] p-1 border border-transparent hover:border-slate-200 rounded outline-none text-slate-600 cursor-pointer bg-white">
                            <option>Le...</option>
                        </select>
                        <div className="w-px h-4 bg-slate-200 mx-1"></div>
                        <button className="p-1.5 hover:bg-slate-100 rounded text-slate-600 font-bold" title="Bold">B</button>
                        <button className="p-1.5 hover:bg-slate-100 rounded text-slate-600 italic" title="Italic">I</button>
                        <button className="p-1.5 hover:bg-slate-100 rounded text-slate-600 underline" title="Underline">U</button>
                        <button className="p-1.5 hover:bg-slate-100 rounded text-slate-600 line-through" title="Strikethrough">T</button>
                        <div className="w-px h-4 bg-slate-200 mx-1"></div>
                        <button className="flex gap-1 p-1 items-center text-slate-600 hover:bg-slate-100 rounded">
                            <div className="w-3 h-0.5 bg-current"></div>
                            <div className="w-3 h-0.5 bg-current"></div>
                            <div className="w-3 h-0.5 bg-current"></div>
                        </button>
                        <button className="flex gap-1 p-1 items-center text-slate-600 hover:bg-slate-100 rounded">
                            ∞
                        </button>
                    </div>

                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Typ hier de notitie..."
                        className="flex-1 w-full bg-white p-4 text-[13px] text-slate-800 outline-none resize-none"
                    />
                </div>
                
                <div className="mt-4 flex justify-between items-center">
                    <span className="text-[10px] text-slate-400">Automatisch opgeslagen concept: zojuist</span>
                    <button 
                        onClick={handleSave} 
                        disabled={isSaving || !content.trim()}
                        className="flex items-center gap-2 bg-[#0F172A] text-white px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition-all disabled:opacity-50"
                    >
                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Opslaan
                    </button>
                </div>
            </div>

            {/* Right Column: Metadata */}
            <div className="w-full lg:w-2/5 flex flex-col p-6 h-full overflow-y-auto custom-scrollbar relative">
                
                {/* Close Button matching Bullhorn style (top right of the right column) */}
                <button onClick={onClose} className="absolute top-4 right-4 z-10 p-1 text-slate-400 hover:text-red-500 rounded transition-colors">
                    <X size={18} />
                </button>

                <div className="space-y-6 mt-4 lg:mt-0">
                    
                    {/* Over Select */}
                    <div>
                        <label className="text-[12px] font-black text-slate-400 uppercase tracking-widest block mb-2">Over</label>
                        <div className="relative flex items-center border-b border-slate-300 focus-within:border-[#0073e6] transition-colors pb-1 group">
                            <Check size={14} className="text-emerald-500 mr-2 shrink-0"/>
                            <select 
                                value={linkedCandidateId} 
                                onChange={e => setLinkedCandidateId(e.target.value)}
                                disabled={mode === 'candidate'} // Can't change candidate if opened FROM a candidate
                                className="w-full appearance-none outline-none text-[14px] text-slate-800 bg-transparent block cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                <option value="">Kies kandidaat...</option>
                                {candidates.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                            {linkedCandidateId && mode !== 'candidate' && <button onClick={() => setLinkedCandidateId('')} className="p-1 text-slate-400 hover:text-red-500 ml-auto transition-colors"><X size={14}/></button>}
                        </div>
                    </div>

                    {/* Actie Select */}
                    <div>
                        <label className="text-[12px] font-black text-slate-400 uppercase tracking-widest block mb-2">Actie</label>
                        <div className="relative border-b border-slate-300 focus-within:border-[#0073e6] transition-colors pb-1">
                            <select 
                                value={action} 
                                onChange={e => setAction(e.target.value)}
                                className="w-full appearance-none outline-none text-[14px] text-slate-800 bg-transparent block cursor-pointer"
                            >
                                {ACTIONS.map(act => (
                                    <option key={act} value={act}>{act}</option>
                                ))}
                            </select>
                            <svg className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                    </div>

                    {/* Aanvullende referenties */}
                    <div>
                        <label className="text-[12px] font-black text-slate-400 uppercase tracking-widest block mb-2">Aanvullende referenties (Vacature)</label>
                        
                        {linkedVacancyId ? (
                            <div className="relative border-b border-red-400 pb-1 mb-1 focus-within:border-red-500">
                                <div className="flex items-center gap-2 bg-white flex-wrap">
                                    <span className="flex items-center gap-2 bg-slate-100 border border-slate-200 text-xs font-bold text-slate-700 px-3 py-1 rounded-full inline-flex">
                                        <span className="w-2.5 h-2.5 rounded-full bg-red-400"></span>
                                        {vacancies.find(v => v.id === linkedVacancyId)?.title || 'Geselecteerde vacature'}
                                        {mode !== 'vacancy' && (
                                            <X size={12} className="text-slate-400 ml-1 cursor-pointer hover:text-red-500" onClick={() => setLinkedVacancyId('')}/>
                                        )}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="relative border-b border-slate-300 pb-1 mb-1 focus-within:border-[#0073e6]">
                                <select 
                                    value={linkedVacancyId} 
                                    onChange={e => setLinkedVacancyId(e.target.value)}
                                    className="w-full appearance-none outline-none text-[14px] text-slate-800 bg-transparent block cursor-pointer"
                                >
                                    <option value="">Koppel aan vacature...</option>
                                    {vacancies.map(v => (
                                        <option key={v.id} value={v.id}>{v.title}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        
                        <div className="flex justify-end mt-1">
                            <span 
                                onClick={() => { if (mode !== 'vacancy') setLinkedVacancyId('') }}
                                className={`text-[9px] font-black text-red-500 uppercase tracking-widest hover:underline ${mode === 'vacancy' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            >
                                Alles wissen X
                            </span>
                        </div>
                    </div>

                    {/* Volgende actie plannen */}
                    <div>
                        <label className="text-[12px] font-black text-slate-400 uppercase tracking-widest block mb-3">Volgende actie plannen</label>
                        <div className="flex border border-[#0073e6] rounded-md overflow-hidden max-w-max shadow-sm">
                            <button 
                                onClick={() => setNextAction('Geen')}
                                className={`px-5 py-2 text-xs font-bold transition-colors uppercase tracking-widest ${nextAction === 'Geen' ? 'bg-[#0073e6] text-white' : 'bg-white text-[#0073e6] hover:bg-blue-50'}`}
                            >Geen</button>
                            <button 
                                onClick={() => setNextAction('Afspraak')}
                                className={`px-5 py-2 text-xs font-bold border-l border-[#0073e6] transition-colors uppercase tracking-widest ${nextAction === 'Afspraak' ? 'bg-[#0073e6] text-white' : 'bg-white text-[#0073e6] hover:bg-blue-50'}`}
                            >Afspraak</button>
                            <button 
                                onClick={() => setNextAction('Taak')}
                                className={`px-5 py-2 text-xs font-bold border-l border-[#0073e6] transition-colors uppercase tracking-widest ${nextAction === 'Taak' ? 'bg-[#0073e6] text-white' : 'bg-white text-[#0073e6] hover:bg-blue-50'}`}
                            >Taak</button>
                        </div>
                    </div>
                    
                    {/* E-mailmelding */}
                    <div className="pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 p-2.5 rounded-lg mb-4">
                            <Mail size={16} className="text-slate-500"/>
                            <span className="text-[12px] font-black text-slate-700 tracking-wide uppercase">E-mailmelding</span>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="relative border-b border-slate-300 pb-1">
                                <input 
                                    type="email" 
                                    placeholder="E-mailadres (bijv. collega@bedrijf.nl)" 
                                    value={notifyEmail}
                                    onChange={(e) => setNotifyEmail(e.target.value)}
                                    className="w-full bg-transparent outline-none text-[13px]" 
                                />
                                <Search size={14} className="absolute right-0 top-1/2 -translate-y-1/2 text-slate-300" />
                            </div>
                            <div className="relative border-b border-slate-300 pb-1 opacity-50 cursor-not-allowed">
                                <input type="text" placeholder="Distributielijst (binnenkort)" disabled className="w-full bg-transparent outline-none text-[13px] cursor-not-allowed" />
                            </div>
                        </div>
                    </div>
                    
                </div>
            </div>
        </div>
    );
};
